import { Cylinder, Frame, GOLD, Shadow } from "./hero-art-parts";

const INK = "color-mix(in oklab, var(--brand) 55%, black)";
const SOFT = "color-mix(in oklab, var(--brand-foreground) 35%, transparent)";
const WHITE = "var(--brand-foreground)";
const SHIELD = "M0 -84L62 -60V4C62 44 32 72 0 90C-32 72 -62 44 -62 4V-60Z";

/** A shield with a padlock: your private way in. Packets go in on the left and come out on the right. */
export function VpnScene() {
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={104} rx={110} ry={20} />
        <path d="M-150 20H-60M60 20H150" stroke={SOFT} strokeWidth="3" strokeDasharray="6 6" className="ha-flow" />
        {[0, 1.5].map((d) => <circle key={d} r="4" fill={GOLD} className="ha-packet" style={{ offsetPath: 'path("M-150 20H150")', animationDelay: `${d}s` }} />)}
        <rect x="-158" y="-6" width="40" height="30" rx="7" fill={WHITE} /><rect x="-166" y="26" width="56" height="5" rx="2.5" fill={WHITE} opacity=".7" />
        <g className="ha-float"><path d={SHIELD} fill={WHITE} /><path d={SHIELD} stroke={GOLD} strokeWidth="2.5" className="ha-ping" />
          <rect x="-20" y="-6" width="40" height="34" rx="7" fill={INK} /><path d="M-11 -6v-10a11 11 0 0 1 22 0v10" stroke={INK} strokeWidth="6" /><circle cy="11" r="5" fill={GOLD} className="ha-pulse" /></g>
        {[-1, 0, 1].map((k) => <rect key={k} x="120" y={-6 + k * 16} width="38" height="12" rx="4" fill={WHITE} opacity=".85" />)}
      </g>
    </Frame>
  );
}

/** Three disks with a loop of arrows around them: copies made, kept and restorable. */
export function BackupScene() {
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={92} rx={100} ry={22} />
        <g className="ha-rot"><circle r="118" stroke={SOFT} strokeWidth="3" strokeDasharray="10 10" /><path d="M118 0l-9 -15 18 0z" fill={GOLD} /></g>
        {[-34, 0, 34].map((cy, i) => <Cylinder key={cy} cx={0} cy={cy} rx={64} ry={20} h={24} className="ha-rise" style={{ animationDelay: `${i * 0.2}s` }} />)}
        {[-34, 0, 34].map((cy, i) => <circle key={cy} cx="38" cy={cy + 34} r="3.5" fill={i === 1 ? GOLD : "#6ee7a8"} className="ha-blink" style={{ animationDelay: `${i * 0.5}s` }} />)}
        <g transform="translate(78 -70)"><g className="ha-float"><circle r="22" fill={GOLD} /><path d="M-9 1l7 7 12 -14" stroke={INK} strokeWidth="5" /></g></g>
      </g>
    </Frame>
  );
}

/** A browser address bar typing a name, with domain endings floating around it. */
export function DomainsScene() {
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={86} rx={120} ry={20} />
        <g className="ha-float"><rect x="-120" y="-56" width="240" height="116" rx="18" fill={WHITE} /><circle cx="-100" cy="-38" r="4" fill={GOLD} /><circle cx="-86" cy="-38" r="4" fill={SOFT} />
          <rect x="-104" y="-22" width="208" height="32" rx="16" fill={INK} opacity=".85" /><circle cx="-86" cy="-6" r="7" stroke={GOLD} strokeWidth="2.5" />
          <rect x="-70" y="-10" width="120" height="8" rx="4" fill={WHITE} className="ha-type" /><rect x="-104" y="22" width="150" height="8" rx="4" fill={INK} opacity=".25" /><rect x="-104" y="38" width="100" height="8" rx="4" fill={INK} opacity=".25" /></g>
        {[[".com", -110, -90, 0], [".eu", 70, -100, 1], [".net", 100, 86, 2], [".org", -120, 82, 3]].map(([t, x, y, i]) => (
          <g key={t as string} className="ha-float2" style={{ animationDelay: `${(i as number) * -1.3}s` }}><rect x={x as number} y={y as number} width="54" height="26" rx="13" fill={GOLD} /><text x={(x as number) + 27} y={(y as number) + 18} textAnchor="middle" fontSize="14" fontWeight="800" fill={INK}>{t as string}</text></g>
        ))}
      </g>
    </Frame>
  );
}

/** An envelope with a letter rising out of it and small letters flying off. */
export function EmailScene() {
  return (
    <Frame>
      <g transform="translate(160 176)">
        <Shadow x={0} y={80} rx={110} ry={20} />
        <path d="M-150 -70H-110M-150 -40H-90M130 70H170" stroke={SOFT} strokeWidth="3" className="ha-flow" strokeDasharray="8 8" />
        <g className="ha-float"><rect x="-64" y="-86" width="128" height="84" rx="8" fill={WHITE} opacity=".7" /><rect x="-44" y="-70" width="60" height="6" rx="3" fill={INK} opacity=".4" /><rect x="-44" y="-56" width="88" height="6" rx="3" fill={INK} opacity=".3" />
          <rect x="-100" y="-40" width="200" height="124" rx="16" fill={WHITE} /><path d="M-96 -32L0 36L96 -32" stroke={INK} strokeWidth="5" opacity=".85" />
          <circle cx="0" cy="52" r="20" fill={GOLD} className="ha-pulse" /><text y="60" textAnchor="middle" fontSize="24" fontWeight="800" fill={INK}>@</text></g>
        {[0, 1.4].map((d) => <rect key={d} x="-9" y="-6" width="18" height="12" rx="3" fill={GOLD} className="ha-packet" style={{ offsetPath: 'path("M110 20C160 -20 190 -60 230 -100")', animationDelay: `${d}s` }} />)}
      </g>
    </Frame>
  );
}

/** A browser showing a padlock in its address bar, and a certificate with a renewal ring in front: issued, valid and renewed for you. */
export function SslScene() {
  return (
    <Frame>
      <g transform="translate(160 164)">
        <Shadow x={0} y={110} rx={120} ry={18} />
        <g className="ha-float2">
          <rect x="-130" y="-98" width="220" height="150" rx="16" style={{ fill: "var(--brand-foreground)" }} />
          <circle cx="-114" cy="-82" r="3.5" fill="#f87171" /><circle cx="-102" cy="-82" r="3.5" fill={GOLD} /><circle cx="-90" cy="-82" r="3.5" fill="#6ee7a8" />
          <rect x="-120" y="-68" width="200" height="26" rx="13" fill={INK} opacity=".9" />
          <rect x="-111" y="-60" width="12" height="10" rx="2.5" fill="#6ee7a8" /><path d="M-109 -60v-3a4 4 0 0 1 8 0v3" stroke="#6ee7a8" strokeWidth="2" />
          <text x="-90" y="-50" fontSize="11" fontWeight="700" fill="var(--brand-foreground)">https://your-site.eu</text>
          <rect x="-120" y="-30" width="120" height="10" rx="5" fill={INK} opacity=".3" /><rect x="-120" y="-12" width="190" height="8" rx="4" fill={INK} opacity=".18" /><rect x="-120" y="2" width="150" height="8" rx="4" fill={INK} opacity=".18" /><rect x="-120" y="22" width="70" height="18" rx="9" fill={INK} opacity=".35" />
        </g>
        <g transform="translate(58 44)"><g className="ha-float">
          <rect x="-62" y="-52" width="124" height="98" rx="12" fill={INK} stroke="var(--brand-foreground)" strokeWidth="2" />
          <rect x="-48" y="-38" width="52" height="7" rx="3.5" style={{ fill: "var(--brand-foreground)" }} /><rect x="-48" y="-24" width="68" height="6" rx="3" style={{ fill: "var(--brand-foreground)" }} opacity=".45" /><rect x="-48" y="-12" width="44" height="6" rx="3" style={{ fill: "var(--brand-foreground)" }} opacity=".45" />
          <rect x="-48" y="22" width="60" height="6" rx="3" style={{ fill: "var(--brand-foreground)" }} opacity=".3" />
          <g transform="translate(34 14)"><circle r="26" stroke={GOLD} strokeWidth="2.5" strokeDasharray="8 7" className="ha-rot" /><circle r="19" fill={GOLD} /><path d="M-8 1l6 6 11-13" stroke={INK} strokeWidth="4.5" /></g>
        </g></g>
      </g>
    </Frame>
  );
}

/** A grid of container cubes, each one pulsing in turn: many small parts run as one. */
export function KubernetesScene() {
  const cells = Array.from({ length: 9 }, (_, i) => ({ x: (i % 3) * 64 - 64, y: Math.floor(i / 3) * 64 - 64, d: ((i * 7) % 9) * 0.25 }));
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={104} rx={110} ry={20} />
        <g className="ha-rotr"><circle r="128" stroke={SOFT} strokeWidth="3" strokeDasharray="4 12" /></g>
        <rect x="-108" y="-108" width="216" height="216" rx="26" fill={WHITE} opacity=".18" />
        {cells.map((c) => <rect key={`${c.x}${c.y}`} x={c.x - 22} y={c.y - 22} width="44" height="44" rx="10" fill={WHITE} className="ha-pulse" style={{ animationDelay: `${c.d}s`, transformBox: "fill-box", transformOrigin: "center" }} />)}
        {cells.filter((_, i) => i % 4 === 0).map((c) => <circle key={`${c.x}${c.y}`} cx={c.x} cy={c.y} r="6" fill={GOLD} className="ha-blink" style={{ animationDelay: `${c.d}s` }} />)}
      </g>
    </Frame>
  );
}

/** A bucket on a base with files dropping in. */
export function StorageScene() {
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={100} rx={100} ry={20} />
        <Cylinder cx={0} cy={-6} rx={80} ry={26} h={92} className="ha-float2" />
        {[-36, 0, 36].map((x, i) => <g key={x} className="ha-rise" style={{ animationDelay: `${i * 0.35}s` }}><rect x={x - 14} y={-86 - i * 4} width="28" height="36" rx="5" fill={GOLD} /><rect x={x - 8} y={-78 - i * 4} width="16" height="4" rx="2" fill={INK} /><rect x={x - 8} y={-70 - i * 4} width="12" height="4" rx="2" fill={INK} opacity=".6" /></g>)}
        <rect x="-60" y="38" width="120" height="8" rx="4" fill={INK} opacity=".25" /><rect x="-60" y="38" width="80" height="8" rx="4" fill={GOLD} className="ha-grow" />
      </g>
    </Frame>
  );
}

/** A stack of database cans next to a table, with a query running through it. */
export function DatabaseScene() {
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={100} rx={120} ry={20} />
        <g transform="translate(-62 0)">{[-52, -8, 36].map((cy, i) => <Cylinder key={cy} cx={0} cy={cy} rx={52} ry={16} h={30} className="ha-rise" style={{ animationDelay: `${i * 0.2}s` }} />)}</g>
        <g className="ha-float"><rect x="4" y="-70" width="124" height="120" rx="12" fill={WHITE} /><rect x="4" y="-70" width="124" height="22" rx="12" fill={GOLD} />
          {[0, 1, 2, 3].map((r) => <g key={r}><rect x="14" y={-38 + r * 22} width="28" height="8" rx="4" fill={INK} opacity=".55" /><rect x="52" y={-38 + r * 22} width="66" height="8" rx="4" fill={INK} opacity=".25" /></g>)}
          <rect x="8" y="-14" width="116" height="20" rx="6" fill={GOLD} opacity=".35" className="ha-scan" /></g>
        <path d="M-10 -30C10 -50 -6 -60 20 -80" stroke={GOLD} strokeWidth="3" strokeDasharray="5 6" className="ha-flow" />
      </g>
    </Frame>
  );
}

/** Traffic split by a shield in front of three servers; the bad packets bounce off. */
export function ProtectionScene() {
  return (
    <Frame>
      <g transform="translate(160 170)">
        <Shadow x={0} y={104} rx={110} ry={20} />
        {[-80, 0, 80].map((x, i) => <g key={x} className="ha-float2" style={{ animationDelay: `${i * -0.8}s` }}><rect x={x - 28} y="52" width="56" height="42" rx="8" fill={WHITE} /><circle cx={x - 14} cy="68" r="4" fill="#6ee7a8" className="ha-blink" style={{ animationDelay: `${i * 0.4}s` }} /><rect x={x - 6} y="64" width="26" height="6" rx="3" fill={INK} opacity=".4" /><rect x={x - 18} y="78" width="36" height="6" rx="3" fill={INK} opacity=".25" /></g>)}
        {[-80, 0, 80].map((x, i) => <circle key={x} r="4" fill={GOLD} className="ha-packet" style={{ offsetPath: `path("M0 -20L${x} 52")`, animationDelay: `${i * 0.6}s` }} />)}
        <g className="ha-float"><path d={SHIELD} transform="translate(0 -52) scale(.62)" fill={WHITE} /><path d="M-14 -50l10 12 20 -24" stroke={INK} strokeWidth="6" /></g>
        {[-1, 1].map((k) => <g key={k} className="ha-ping" style={{ animationDelay: `${(k + 1) * 0.5}s` }}><circle cx={k * 110} cy="-80" r="10" fill="#f87171" /><path d={`M${k * 110 - 4} -84l8 8m0 -8l-8 8`} stroke={WHITE} strokeWidth="2.5" /></g>)}
      </g>
    </Frame>
  );
}

/** An update panel: rows that fill up and turn into ticks, a version badge and a refresh ring. Kept current, before it becomes a problem. */
export function MaintenanceScene() {
  const rows = ["System", "Apps", "Security"];
  return (
    <Frame>
      <g transform="translate(160 166)">
        <Shadow x={0} y={108} rx={110} ry={18} />
        <g className="ha-float">
          <rect x="-112" y="-92" width="224" height="170" rx="18" style={{ fill: "var(--brand-foreground)" }} />
          <rect x="-112" y="-92" width="224" height="34" rx="18" fill={INK} opacity=".12" />
          <rect x="-96" y="-80" width="70" height="10" rx="5" fill={INK} opacity=".55" />
          <g transform="translate(70 -75)"><rect x="-32" y="-11" width="64" height="22" rx="11" fill="#6ee7a8" className="ha-pulse" /><path d="M-18 0l5 5 8 -10" stroke={INK} strokeWidth="3" /><rect x="-4" y="-3" width="26" height="6" rx="3" fill={INK} opacity=".7" /></g>
          {rows.map((r, i) => (
            <g key={r} transform={`translate(0 ${-34 + i * 40})`}>
              <rect x="-96" y="-14" width="192" height="32" rx="10" fill={INK} opacity=".08" />
              <circle cx="-78" cy="2" r="9" fill={INK} opacity=".8" /><circle cx="-78" cy="2" r="3.5" fill={GOLD} className="ha-blink" style={{ animationDelay: `${i * 0.5}s` }} />
              <rect x="-60" y="-3" width="64" height="7" rx="3.5" fill={INK} opacity=".45" />
              <rect x="14" y="-2" width="56" height="6" rx="3" fill={INK} opacity=".15" /><rect x="14" y="-2" width="56" height="6" rx="3" fill={GOLD} className="ha-type" style={{ animationDelay: `${i * 0.6}s` }} />
              <g transform="translate(84 2)"><circle r="8" fill="#6ee7a8" /><path d="M-4 0l3 3 5-6" stroke={INK} strokeWidth="2.2" /></g>
            </g>
          ))}
        </g>
        <g transform="translate(96 66)"><g className="ha-float2"><circle r="26" fill={GOLD} /><g className="ha-rot"><path d="M-12 -4a12 12 0 0 1 20 -8M12 4a12 12 0 0 1 -20 8" stroke={INK} strokeWidth="4" /><path d="M10 -18l1 8-8 -1zM-10 18l-1 -8 8 1z" fill={INK} /></g></g></g>
      </g>
    </Frame>
  );
}
